import React, { useMemo } from 'react';
import { Flex, ToolkitFeatureFlag } from '@cko/primitives';
import { useCurrentClientManager } from '@cko/dashboard-shared';
import { SubEntityTooltip } from '../StyledHelperComponents';

import {
  ExpandIconContainer,
  IABreadCrumbButton,
  SubEntityBreadcrumbFont,
} from './AccountSelector.styles';
import { AccountSelectorFullTextTooltip } from './AccountSelectorFullTextTooltip';
import { AccountSelectorTruncatedTextWithoutTooltip } from './AccountSelectorTruncatedTextWithoutTooltip';

const DEFAULT_PARENT_TEXT = 'Account';

// TODO once the icon is available in the toolkit for the IA side menu then can me also be imported in here
const Expand = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="16" viewBox="0 0 12 16" fill="none">
    <path
      d="M1 9.66671L6 14.6667L11 9.66671M1 6.33337L6 1.33337L11 6.33337"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
);

interface AccountSelectorButtonProps {
  onClick: () => void;
}

const ActionSelectIcon = () => (
  <ExpandIconContainer>
    <Expand />
  </ExpandIconContainer>
);

const MIN_LENGTH_PX = '96';
const MAX_LENGTH_PX = '300';

export const AccountSelectorButton = ({ onClick }: AccountSelectorButtonProps) => {
  const { client, entity, subEntity, segment, selectedAccountLevel } = useCurrentClientManager();

  const truncatedTextMaxLength = {
    client: selectedAccountLevel === 'client' ? MAX_LENGTH_PX : MIN_LENGTH_PX,
    entity: selectedAccountLevel === 'entity' ? MAX_LENGTH_PX : MIN_LENGTH_PX,
    segment: selectedAccountLevel === 'segment' ? MAX_LENGTH_PX : MIN_LENGTH_PX,
  };

  const handleOpenMenuClick = () => {
    if (!subEntity) {
      onClick();
    }
  };

  const notTruncatedBreadCrumbText = useMemo(() => {
    const fullText = [client.name];

    [entity, segment].forEach((item) => {
      if (item) {
        fullText.push(` / ${item.name}`);
      }
    });

    return fullText.join('');
  }, [client, entity, segment]);

  if (subEntity) {
    return (
      <Flex
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="center"
        height="60px"
        data-testid="non-clickable-account-selector-button"
        data-analytics-id="account-selector-button"
      >
        <SubEntityBreadcrumbFont data-testid="subEntity-entity-name">
          {subEntity?.entityName || DEFAULT_PARENT_TEXT}
        </SubEntityBreadcrumbFont>
        <SubEntityTooltip maxWidth="300px" testId="subEntity-name">
          {subEntity?.name || subEntity?.referenceNumber}
        </SubEntityTooltip>
      </Flex>
    );
  }

  return (
    <ToolkitFeatureFlag flag="v10">
      <AccountSelectorFullTextTooltip text={notTruncatedBreadCrumbText}>
        <IABreadCrumbButton
          data-analytics-id="account-selector-button"
          data-testid="account-selector-button"
          onClick={handleOpenMenuClick}
        >
          <AccountSelectorTruncatedTextWithoutTooltip
            data-testid="client-name"
            maxWidth={truncatedTextMaxLength.client}
          >
            {client?.name}
          </AccountSelectorTruncatedTextWithoutTooltip>
          {entity?.name && (
            <>
              {selectedAccountLevel !== 'client' && '/'}
              <AccountSelectorTruncatedTextWithoutTooltip
                maxWidth={truncatedTextMaxLength.entity}
                hasBoldText={selectedAccountLevel === 'entity'}
                data-testid="entity-name"
              >
                {entity.name}
              </AccountSelectorTruncatedTextWithoutTooltip>
              {selectedAccountLevel !== 'entity' && '/'}
            </>
          )}
          {segment?.name && (
            <>
              <AccountSelectorTruncatedTextWithoutTooltip
                data-testid="segment-name"
                maxWidth={truncatedTextMaxLength.segment}
                hasBoldText={selectedAccountLevel === 'segment'}
              >
                {segment.name}
              </AccountSelectorTruncatedTextWithoutTooltip>
            </>
          )}
          <ActionSelectIcon />
        </IABreadCrumbButton>
      </AccountSelectorFullTextTooltip>
    </ToolkitFeatureFlag>
  );
};
